import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { DataViewType } from '@connect-services/client/pages/management/data/deposit/deposit.component';
import { DepositListStatusComponent } from '@connect-services/client/pages/management/data/deposit/deposit-list/deposit-list-status/deposit-list-status.component';
import { DepositTableRow, ManagementDataService } from '@connect-services/client/services/management-data.service';
import { TranslateService } from '@connect-services/client/services/translate.service';
import { CustomColumn, CustomTableComponent } from '@connect-services/src/lib/components/table/table.component';
import {
    Deposit,
    DepositControllerGet200Response,
    DepositFolder,
    DepositsService,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { defaultPaginationParameters, OrderEnum, PaginationParameters } from '@shared/client/models/pagination.model';

// Fictives columns to keep alignment between the DepositFolder table and Deposit table.
const ORGANIZATION_DATA_COLUMNS: CustomColumn[] = [
    { key: 'name', label: 'depositName', sortable: false },
    { key: 'version', label: 'version', sortable: false },
    { key: 'fictiveColumnOne', label: '', sortable: false },
    { key: 'createdAt', label: 'importDate', sortable: false },
    { key: 'status', label: 'validation', sortable: false },
    { key: 'fictiveColumnTwo', label: '', sortable: false },
    { key: 'fictiveColumnFour', label: '', sortable: false },
    { key: 'fictiveColumnThree', label: '', sortable: false },
];

const REFERENCE_DATA_COLUMNS: CustomColumn[] = [
    { key: 'name', label: 'depositName', sortable: false },
    { key: 'version', label: 'version', sortable: false },
    { key: 'createdAt', label: 'updateDate', sortable: false },
    { key: 'fictiveColumnThree', label: '', sortable: false },
];

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CustomTableComponent, DepositListStatusComponent, LoadingComponent, RessourcesModule],
    selector: 'app-deposit-list',
    styleUrl: './deposit-list.component.scss',
    templateUrl: './deposit-list.component.html',
})
export class DepositListComponent {
    public readonly depositFolder = input<DepositFolder | undefined>();

    protected readonly customColumns = computed<CustomColumn[]>(() => {
        if (this.managementDataService.currentView() === DataViewType.REFERENCE_DATA) {
            return REFERENCE_DATA_COLUMNS;
        }

        return ORGANIZATION_DATA_COLUMNS;
    });

    protected readonly dataColumn = signal<DepositTableRow[]>([]);
    protected readonly Deposit = Deposit;
    protected readonly deposits = signal<DepositControllerGet200Response>({
        hasNextPage: false,
        hasPreviousPage: false,
        items: [],
        page: 0,
        take: 0,
        totalItems: 0,
        totalPages: 0,
    });
    protected readonly isLoading = signal<boolean>(true);
    protected readonly managementDataService = inject(ManagementDataService);
    protected paginationParameters: PaginationParameters = {
        ...defaultPaginationParameters,
        order: OrderEnum.DESC,
        orderBy: 'version',
    };
    protected readonly translateService = inject(TranslateService);

    private readonly depositsService = inject(DepositsService);

    public constructor() {
        effect(async () => {
            if (
                !this.depositFolder() ||
                !this.managementDataService.expandedElementId() ||
                this.managementDataService.expandedElementId() !== this.depositFolder()!.id
            ) {
                return;
            }

            await this.getDepositList();
        });
    }

    public async getDepositList(): Promise<void> {
        this.deposits.set(
            await firstValueFrom(
                this.depositsService.depositControllerGet({
                    ...this.paginationParameters,
                    depositFolder: this.depositFolder()!.id!,
                }),
            ),
        );

        this.createCustomTable();
    }

    protected async openDepositDetailPanel(deposit: DepositTableRow): Promise<void> {
        if (deposit.status === Deposit.StatusEnum.ABORTED) {
            return;
        }

        await this.managementDataService.openDepositDetailPanel(deposit.id);
    }

    private createCustomTable(): void {
        this.dataColumn.set(
            this.deposits()!
                .items.map((deposit: Deposit): DepositTableRow | undefined => {
                    return {
                        createdAt: deposit.createdAt,
                        hadDataAtImport: deposit.hadDataAtImport,
                        id: deposit.id,
                        isValid: deposit.depositValidation?.isValid,
                        name: deposit.name,
                        status: deposit.status,
                        version: deposit.version,
                    };
                })
                .filter((deposit): deposit is DepositTableRow => {
                    return deposit !== undefined;
                }),
        );

        this.isLoading.set(false);
    }
}
